import React, { createContext, useContext, useState, ReactNode } from 'react';

import { NotificationsProvider } from '../contexts/notificationContext';

// --- Sidebar Provider ---
interface SidebarContextType {
    isCollapsed: boolean;
    toggleSidebar: () => void;
    setCollapsed: (value: boolean) => void;
    isMobileOpen: boolean;
    setIsMobileOpen: (value: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType>({
    isCollapsed: false,
    toggleSidebar: () => { },
    setCollapsed: () => { },
    isMobileOpen: false,
    setIsMobileOpen: () => { },
});

interface ProvidersProps {
    children: ReactNode;
}

export const SidebarProvider: React.FC<ProvidersProps> = ({ children }) => {
    // Desktop collapse state
    const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
        if (globalThis.window !== undefined) {
            const saved = globalThis.window.localStorage.getItem('user_sidebar_collapsed');
            return saved === 'true';
        }
        return false;
    });

    // Mobile open state
    const [isMobileOpen, setIsMobileOpen] = useState<boolean>(false);

    const toggleSidebar = () => {
        setIsCollapsed((prev) => {
            const newState = !prev;
            globalThis.localStorage.setItem('user_sidebar_collapsed', String(newState));
            return newState;
        });
    };

    const setCollapsed = (value: boolean) => {
        setIsCollapsed(value);
        globalThis.localStorage.setItem('user_sidebar_collapsed', String(value));
    };

    return (
        <SidebarContext.Provider
            value={{
                isCollapsed,
                toggleSidebar,
                setCollapsed,
                isMobileOpen,
                setIsMobileOpen,
            }}
        >
            {children}
        </SidebarContext.Provider>
    );
};

export const useSidebar = () => useContext(SidebarContext);

// --- Main App Provider (combines all) ---
export const AppProvider: React.FC<ProvidersProps> = ({ children }) => {
    return (
        <SidebarProvider>
            <NotificationsProvider>
                {children}
            </NotificationsProvider>
        </SidebarProvider>
    );
};
